/**
 * ELF-CIRCLE footer — section `#11d`, the motion wall.
 *
 * Oversized outlined wordmark over a full-bleed backdrop, four link columns,
 * a subscribe strip and the legal row.
 *
 * A looping video plays behind this. The `footer.backdrop` image sits under
 * it and shows until the video starts, or wherever it can't play.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { ImageUp, LogIn, LogOut } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import EditableImage from './EditableImage';
import { useLandingImages } from './useLandingImages';
import { useConsultOpener } from './elfConsult';
import { useInViewVideo } from './useInViewVideo';
import { pullToTop, warmTopPull } from './topPull';
import { useAuth } from '../../../auth';
import { route } from '../../../../shared/utils';
import footerVideo from '../../../../assets/elf/footer-wall.mp4';

interface Column {
  head: string;
  links: string[];
}

const COLUMNS: Column[] = [
  { head: 'Company', links: ['About us', 'Mission', 'Team', 'Careers', 'Press'] },
  {
    head: 'Services',
    links: ['Marketing', 'Web development', 'Events', 'Consultation', 'Request a proposal'],
  },
  {
    head: 'Shop & events',
    links: ['Merchandise', 'Event calendar', 'Tickets', 'Lookbook', 'Gift cards'],
  },
  {
    head: 'The network',
    links: ['Sponsors', 'Partners', 'Newsletter', 'Join the circle', 'elfcity.org'],
  },
];

const SOCIALS: { label: string; url: string }[] = [
  { label: 'Instagram', url: 'https://instagram.com/elfcircle' },
  { label: 'TikTok', url: 'https://tiktok.com/@elfcircle' },
  { label: 'YouTube', url: 'https://youtube.com/@elfcircle' },
  { label: 'X', url: 'https://x.com/elfcircle' },
];

const LEGAL = ['Privacy', 'Terms', 'Cookies', 'Accessibility'];

export const ElfFooter: React.FC = () => {
  const videoRef = useInViewVideo();
  const location = useLocation();
  const { canEdit } = useLandingImages();
  const { isAuthenticated, logout } = useAuth();
  const bookConsult = useConsultOpener({ source: 'footer', title: 'Book a consultation' });
  const startProject = useConsultOpener({ source: 'footer.project', title: 'Start a project' });
  const subscribe = useConsultOpener({ source: 'footer.subscribe', title: 'Get the drop first' });

  // The theme bar, hero switch and Vizor launcher are fixed to the bottom of
  // the screen, exactly where the last footer row comes to rest. Measure how
  // much of the viewport they take and keep the sign-in and back-to-top
  // buttons clear of it — the bar wraps on a tablet, so no fixed number works.
  const footRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const foot = footRef.current;
    if (!foot) return;
    const measure = () => {
      let top = window.innerHeight;
      document.querySelectorAll('.elf-bar, .elf-hswitch, .vizor__launch').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height && getComputedStyle(el).position !== 'static') top = Math.min(top, r.top);
      });
      foot.style.setProperty('--foot-clear', `${Math.round(window.innerHeight - top + 14)}px`);
    };
    measure();
    // Vizor mounts late and the bar reflows; the footer arriving on screen is
    // the moment the answer matters, and also when to fetch the mascot.
    const io =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            measure();
            void warmTopPull();
          })
        : null;
    io?.observe(foot);
    window.addEventListener('resize', measure);
    return () => {
      io?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // The mascot hauls the page back up — see topPull.ts.
  const toTop = useCallback(() => {
    void pullToTop();
  }, []);

  return (
    <footer ref={footRef} className="elf-foot">
      <EditableImage slot="footer.backdrop" className="elf-foot__bg" alt="" />
      {/* Muted and inline, or phones refuse to autoplay it. */}
      <video
        ref={videoRef}
        className="elf-foot__bg elf-foot__video"
        src={footerVideo}
        loop
        muted
        playsInline
        preload="none"
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="elf-foot__scrim" />
      <div className="sigrule elf-foot__rule" />

      <div className="elf-foot__layer">
        <div className="elf-foot__top">
          <div className="fbig">Where culture lives</div>
          <div className="elf-foot__intro">
            <p>
              Marketing, web development, events, merch and consultations — one hub for artists,
              models and music creators.
            </p>
            <div className="elf-foot__intro-cta">
              <button className="pillghost" type="button" onClick={bookConsult}>
                Book a consultation
              </button>
              <button className="pillcta mag" type="button" onClick={startProject}>
                Start a project <span>→</span>
              </button>
            </div>
          </div>
        </div>

        <div className="elf-foot__cols">
          <div className="elf-foot__brand">
            <EditableImage slot="brand.logo" alt="ELF-CIRCLE" />
            <p>The service hub for the ELF world. Built with VOILACODE.</p>
            <div className="elf-foot__socials">
              {SOCIALS.map((s) => (
                <a
                  className="fsoc"
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{s.label}</span>
                  <span>↗</span>
                </a>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div className="fcol" key={col.head}>
              <div className="elf-foot__colhead">{col.head}</div>
              {col.links.map((link) => (
                <a href="#" key={link}>
                  {link}
                </a>
              ))}
            </div>
          ))}
        </div>

        <div className="elf-foot__sub">
          <div>
            <div className="elf-foot__sub-title">Get the drop first</div>
            <div className="elf-foot__sub-note">Events, releases and openings. No noise.</div>
          </div>
          <input className="fld" placeholder="Email or mobile #" aria-label="Email or mobile" />
          <button className="pillcta mag" type="button" onClick={subscribe}>
            Subscribe <span>→</span>
          </button>
        </div>

        <div className="elf-foot__legal">
          <span>© 2026 ELF-CIRCLE. Built with VOILACODE.</span>
          <div className="fcol">
            {LEGAL.map((l) => (
              <a href="#" key={l}>
                {l}
              </a>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          {canEdit && (
            <span className="elf-editmode" title="Hover any image to replace it">
              <ImageUp size={13} aria-hidden="true" />
              Edit mode
            </span>
          )}
          {isAuthenticated ? (
            /* Signing out lands back here rather than on a login screen, so
               the edits can be checked straight away as a visitor sees them. */
            <button
              type="button"
              className="flogin"
              title="Sign out"
              aria-label="Sign out"
              onClick={() => logout('/')}
            >
              <LogOut size={18} strokeWidth={2.2} aria-hidden="true" />
            </button>
          ) : (
            <Link
              className="flogin"
              /* `from` is the same intent a route-guard bounce sets, so login
                 returns here instead of falling through to /admin. */
              to={route('/auth/login')}
              state={{ from: { pathname: location.pathname } }}
              title="Sign in"
              aria-label="Sign in"
            >
              <LogIn size={18} strokeWidth={2.2} aria-hidden="true" />
            </Link>
          )}
          <button
            className="ftop"
            type="button"
            title="Back to top"
            aria-label="Back to top"
            onClick={toTop}
            // Fetch the mascot's frames as soon as a press looks likely.
            onPointerEnter={() => void warmTopPull()}
            onFocus={() => void warmTopPull()}
          >
            ↑
          </button>
        </div>
      </div>
    </footer>
  );
};

export default ElfFooter;
